import {
  PIECHART_ANGLE_OFFSET_DEGREES,
  PIECHART_FULL_CIRCLE_DEGREES,
  PIECHART_HALF_TURN_DEGREES,
} from './piechart-constants';

interface PieChartPoint {
  x: number;
  y: number;
}

interface PolarToCartesianInput {
  centerX: number;
  centerY: number;
  radius: number;
  angleDeg: number;
}

interface DonutSectorPathInput {
  centerX: number;
  centerY: number;
  outerRadius: number;
  innerRadius: number;
  startDeg: number;
  endDeg: number;
}

interface DonutArcPathInput {
  centerX: number;
  centerY: number;
  radius: number;
  startDeg: number;
  endDeg: number;
}

function polarToCartesian(input: PolarToCartesianInput): PieChartPoint {
  const radians = ((input.angleDeg - PIECHART_ANGLE_OFFSET_DEGREES) * Math.PI)
    / PIECHART_HALF_TURN_DEGREES;

  return {
    x: input.centerX + input.radius * Math.cos(radians),
    y: input.centerY + input.radius * Math.sin(radians),
  };
}

function getLargeArcFlag(input: { startDeg: number; endDeg: number }): number {
  return input.endDeg - input.startDeg > PIECHART_HALF_TURN_DEGREES ? 1 : 0;
}

export function describeDonutSector(input: DonutSectorPathInput): string {
  const outerStart = polarToCartesian({
    centerX: input.centerX,
    centerY: input.centerY,
    radius: input.outerRadius,
    angleDeg: input.startDeg,
  });
  const outerEnd = polarToCartesian({
    centerX: input.centerX,
    centerY: input.centerY,
    radius: input.outerRadius,
    angleDeg: input.endDeg,
  });
  const innerStart = polarToCartesian({
    centerX: input.centerX,
    centerY: input.centerY,
    radius: input.innerRadius,
    angleDeg: input.startDeg,
  });
  const innerEnd = polarToCartesian({
    centerX: input.centerX,
    centerY: input.centerY,
    radius: input.innerRadius,
    angleDeg: input.endDeg,
  });
  const largeArcFlag = getLargeArcFlag(input);

  return `M ${outerStart.x} ${outerStart.y} A ${input.outerRadius} ${input.outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y} L ${innerEnd.x} ${innerEnd.y} A ${input.innerRadius} ${input.innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y} Z`;
}

export function describeArc(input: DonutArcPathInput): string {
  const start = polarToCartesian({
    centerX: input.centerX,
    centerY: input.centerY,
    radius: input.radius,
    angleDeg: input.endDeg,
  });
  const end = polarToCartesian({
    centerX: input.centerX,
    centerY: input.centerY,
    radius: input.radius,
    angleDeg: input.startDeg,
  });
  const largeArcFlag = getLargeArcFlag(input);

  return `M ${start.x} ${start.y} A ${input.radius} ${input.radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

export function getAvailableDegrees(input: {
  sliceCount: number;
  gapDegrees: number;
  capDegrees?: number;
}): number {
  if (input.sliceCount <= 1) {
    return PIECHART_FULL_CIRCLE_DEGREES;
  }

  const capDegrees = input.capDegrees ?? 0;
  const totalGap = input.sliceCount * (input.gapDegrees + capDegrees * 2);
  return PIECHART_FULL_CIRCLE_DEGREES - totalGap;
}
